import { ErrorBoundary } from "@sentry/react";
import React, { useMemo } from "react";
import { ErrorMsg, NoDataMsg } from "../../../components/AppMessages";
import { useQuery } from "@tanstack/react-query";
import { withRouter, RouteComponentProps } from "react-router-dom";

interface Props {
    queryFn: () => Promise<any>;
    summaryData?: any;
}

const MarketOverviewTable = withRouter((props: RouteComponentProps & Props) => {
    const { queryFn, summaryData, history } = props;

    const searchKey = React.useMemo(() => {
        const p = new URLSearchParams(history.location.search);
        const dateRange = p.get("date_range") ?? "";
        const promptType = p.get("prompt_type") ?? "";
        return `date_range=${dateRange}&prompt_type=${promptType}`;
    }, [history.location.search]);

    const { data, isFetching, refetch } = useQuery({
        queryKey: ["geoReportingMarketOverview", searchKey],
        queryFn: () => queryFn(),
        enabled: false,
    });

    React.useEffect(() => {
        const urlDateRange = new URLSearchParams(history.location.search).get("date_range");
        if (urlDateRange) refetch();
    }, [searchKey]);

    const rows: any[] = useMemo(() => Array.isArray(data) ? data : [], [data]);

    const regionGroups: { region: string; rows: any[] }[] = useMemo(() => {
        const map = new Map<string, any[]>();
        for (const row of rows) {
            const region = row.region ?? "AMIEO";
            if (!map.has(region)) map.set(region, []);
            const bucket = map.get(region);
            if (bucket) bucket.push(row);
        }
        const regionOrder = ["NAE", "AMIEO"];
        return Array.from(map.entries())
            .map(([region, rows]) => ({ region, rows }))
            .sort((a, b) => {
                const ai = regionOrder.indexOf(a.region);
                const bi = regionOrder.indexOf(b.region);
                const aIdx = ai === -1 ? regionOrder.length : ai;
                const bIdx = bi === -1 ? regionOrder.length : bi;
                return aIdx - bIdx;
            });
    }, [rows]);

    const renderCell = (value: number | null | undefined, _threshold: number | null | undefined, key: string) => (
        <td key={key}>
            {typeof value === "number" && value !== 0 ? `${value.toFixed(1)}%` : "-"}
        </td>
    );

    const hasData = rows.length > 0;

    const formatRegionLabel = (region: string) => {
        if (region === "NAE") return "EUROPE";
        if (region === "AMIEO") return "AMIO";
        return region;
    };

    const renderTable = (region: string, groupRows: any[]) => (
        <table key={region} style={{ marginBottom: "1.5em" }}>
            <thead>
                <tr>
                    <th rowSpan={2}>{formatRegionLabel(region)}</th>
                    {groupRows.map((item: any, i: number) => (
                        <th key={`market-${region}-${i}`} colSpan={2}>{item.market}</th>
                    ))}
                </tr>
                <tr>
                    {groupRows.map((_: any, i: number) => (
                        <React.Fragment key={`sub-${region}-${i}`}>
                            <th>SOV</th>
                            <th>Avg Sentiment</th>
                        </React.Fragment>
                    ))}
                </tr>
            </thead>
            <tbody>
                <tr>
                    <td className="italic">Selected period</td>
                    {groupRows.map((item: any, i: number) => (
                        <React.Fragment key={`sel-${region}-${i}`}>
                            {renderCell(item.selected_period_sov, summaryData?.sov, `sel-sov-${region}-${i}`)}
                            {renderCell(item.selected_period_average_sentiment, summaryData?.average_sentiment, `sel-as-${region}-${i}`)}
                        </React.Fragment>
                    ))}
                </tr>
                <tr>
                    <td className="italic">Previous period</td>
                    {groupRows.map((item: any, i: number) => (
                        <React.Fragment key={`prev-${region}-${i}`}>
                            {renderCell(item.previous_period_sov, summaryData?.sov, `prev-sov-${region}-${i}`)}
                            {renderCell(item.previous_period_average_sentiment, summaryData?.average_sentiment, `prev-as-${region}-${i}`)}
                        </React.Fragment>
                    ))}
                </tr>
            </tbody>
        </table>
    );

    return (
        <ErrorBoundary fallback={<ErrorMsg />}>
            <div className="tableContainer market-overview-scroll">
                {isFetching ? null : hasData ? (
                    regionGroups.map(({ region, rows: groupRows }) => renderTable(region, groupRows))
                ) : (
                    <div className="no_content"><NoDataMsg /></div>
                )}
            </div>
        </ErrorBoundary>
    );
});

export default MarketOverviewTable;